import React, { useEffect, useState, useMemo } from "react";
import {
  Drawer,
  DrawerBody,
  DrawerHeader,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  Box,
  Text,
  Spinner,
  VStack,
  HStack,
  Badge,
  Divider,
  Center,
  Wrap,
  WrapItem,
  Input,
  InputGroup,
  InputLeftElement,
  Collapse,
  Heading,
} from "@chakra-ui/react";
import { SearchIcon } from "@chakra-ui/icons";
import { appRequest } from "routes";

interface WorkflowDocumentCountDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  level: string;
  getDefaultWorkflow?: any;
}

const WorkflowDocumentCountDrawer: React.FC<
  WorkflowDocumentCountDrawerProps
> = ({ isOpen, onClose, level, getDefaultWorkflow }) => {
  const [documentCounts, setDocumentCounts] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState("");

  const getCounts = async () => {
    try {
      setLoading(true);
      const response: any = await appRequest(
        "document",
        "getWorkflowDocumentCount",
        {}
      );
      setDocumentCounts(response?.data || []);
    } catch (err) {
      console.error("Error fetching document count", err);
      setDocumentCounts([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      setSearchText(""); // reset search
      getCounts();
    }
  }, [isOpen]);

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case "pending":
        return "orange";
      case "approved":
        return "green";
      case "rejected":
        return "red";
      default:
        return "gray";
    }
  };

  const filteredData = useMemo(() => {
    return documentCounts
      .filter((item) =>
        item.workflowName?.toLowerCase().includes(searchText.toLowerCase())
      )
      .filter((item) =>
        Object.values(item).some((entry: any) => {
          return typeof entry === "object" && entry?._id?.level === level;
        })
      );
  }, [searchText, documentCounts, level]);

  return (
    <Drawer isOpen={isOpen} placement="right" onClose={onClose} size="xl">
      <DrawerOverlay />
      <DrawerContent>
        <DrawerCloseButton />
        <DrawerHeader>
          <VStack align="start">
            <Heading size="md">Workflow Document Counts</Heading>
          </VStack>
        </DrawerHeader>
        <DrawerBody>
          {loading ? (
            <Center h="70vh">
              <VStack spacing={4}>
                <Spinner size="xl" />
                <Text fontSize="md">Fetching document counts...</Text>
              </VStack>
            </Center>
          ) : (
            <>
              {/* Search Input */}
              <InputGroup mb={6}>
                <InputLeftElement pointerEvents="none">
                  <SearchIcon color="gray.400" />
                </InputLeftElement>
                <Input
                  placeholder="Search by workflow name..."
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                />
              </InputGroup>

              {/* Workflow Cards */}
              <Collapse in={!loading && filteredData.length > 0} animateOpacity>
                <VStack align="stretch" spacing={5}>
                  {filteredData.map((item, index) => {
                    const { workflowName, ...statuses } = item;

                    const levelStatuses = Object.values(statuses).filter(
                      (status: any) =>
                        typeof status === "object" &&
                        status._id?.level === level
                    );

                    if (levelStatuses.length === 0) return null;

                    return (
                      <Box
                        key={index}
                        borderWidth="1px"
                        borderRadius="lg"
                        p={4}
                        shadow="sm"
                        bg="gray.50"
                      >
                        <Text
                          fontWeight="bold"
                          fontSize="lg"
                          mb={2}
                          cursor="pointer"
                          role="button"
                          tabIndex={0}
                          onClick={() => {
                            const id = item?._id;
                            if (!id) return;

                            sessionStorage.setItem("activeWorkflowId", id);
                            const storedId =
                              sessionStorage.getItem("activeWorkflowId");

                            if (storedId === id) {
                              window.location.reload();
                            }
                          }}
                        >
                          {workflowName?.trim() || "Unnamed Workflow"}
                        </Text>
                        <Divider mb={2} />

                        <Wrap spacing={3}>
                          {levelStatuses.map((status: any, i) => (
                            <WrapItem key={i}>
                              <Badge
                                colorScheme={getStatusColor(status._id.status)}
                                px={3}
                                py={1}
                                borderRadius="md"
                                fontSize="sm"
                              >
                                {status._id.status.toUpperCase()}:{" "}
                                {status.count}
                              </Badge>
                            </WrapItem>
                          ))}
                        </Wrap>
                      </Box>
                    );
                  })}
                </VStack>
              </Collapse>

              {/* No Match Found */}
              {!loading && filteredData.length === 0 && (
                <Center mt={20}>
                  <Text color="gray.500">
                    No workflows match your search or selected level.
                  </Text>
                </Center>
              )}
            </>
          )}
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );
};

export default WorkflowDocumentCountDrawer;
